import { LocalizationSerializer } from "@serializers/localization"

import { BaseHttpProvider } from "./base-http.provider"

import type { LanguageISO } from "./types"
import type { VacancyHeadType } from "@features/vacancies/molecules/vacancy-head"

export class VacanciesProvider extends BaseHttpProvider {
  async getCareersTemplates(lang: LanguageISO) {
    const data = await this.execute({
      uri: "/vacancies-page",
      query: {
        lang,
      },
    })

    return {
      templates: data?.templates.map((template) =>
        LocalizationSerializer(template),
      ),
    }
  }

  async getVacancies() {
    const data = await this.execute({
      uri: "/vacancies-v2",
    })
    return {
      vacancies: data?.vacancies ?? [],
    }
  }

  async getVacancy(id: string | number) {
    const data = await this.execute({
      uri: `/vacancies-v2/${id}`,
    })
    return data
  }

  async sendRequest(params: {
    name: string
    surname: string
    email: string
    phone: string
    linkedin: string
    source: string
    files: File[]
    vacancy_id: string
    vacancy_title: string
  }): Promise<{
    applicationSent: boolean
    emailSent?: boolean
    error?: Error
  }> {
    const {
      name,
      surname,
      email,
      phone,
      linkedin,
      source,
      files,
      vacancy_id,
      vacancy_title,
    } = params

    if (files.length > 0) {
      const formData = new FormData()
      for (const file of files) {
        formData.append("files", file, file.name)
      }
      const media = await this.execute({
        uri: "/upload",
        method: "POST",
        isMultiPartData: true,
        formData,
      }).catch()

      if (media) {
        const applicationResponse = await this.execute({
          uri: "/vacancy-request-v2",
          method: "POST",
          data: {
            name,
            surname,
            email,
            phone,
            link: linkedin,
            source,
            attachments: JSON.stringify(media),
            vacancy_id: vacancy_id,
            vacancy_title: vacancy_title,
          },
        })
        return {
          applicationSent: Boolean(applicationResponse.applicationSent),
          emailSent: Boolean(applicationResponse.emailSent),
          error: applicationResponse.error,
        }
      }
    } else {
      const applicationResponse = await this.execute({
        uri: "/vacancy-request-v2",
        method: "POST",
        data: {
          name,
          surname,
          email,
          phone,
          link: linkedin,
          source,
          attachments: JSON.stringify(files),
          vacancy_id: vacancy_id,
          vacancy_title: vacancy_title,
        },
      })
      return {
        applicationSent: Boolean(applicationResponse.applicationSent),
        emailSent: Boolean(applicationResponse.emailSent),
        error: applicationResponse.error,
      }
    }

    return { applicationSent: false }
  }
}

interface Manager {
  full_name: string
  position: string
  greeting: string
  photo: {
    url: string
  }
}
export interface Vacancy {
  id: number
  order: number
  title: string
  location: string
  skills?: string
  salary: string
  is_new?: boolean
  is_full_time: boolean
  vacancy_head: VacancyHeadType
  manager?: Manager
  department_id: number
  description: { title: string; text: string }[]
  locale: string
  localizations: { id: number; locale: string; published_at?: string }[]
}
